import React from 'react';
import { CheckCircle, ArrowRight, AlertCircle, Rocket } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const GettingStarted: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Quick Start Guide"
        description="Welcome to your CRM! This guide will help you set up your account and start capturing leads within minutes."
        icon={<Rocket className="w-6 h-6" />}
        badge="Essential"
        badgeColor="green"
      />

      <div className="p-8 pb-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* Overview */}
      <div className="not-prose bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-brandColor dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">What You'll Learn</h3>
            <ul className="space-y-1 text-sm text-green-800 dark:text-green-300">
              <li>✓ Understanding your CRM dashboard</li>
              <li>✓ Setting up your first integration</li>
              <li>✓ Configuring lead pipeline</li>
              <li>✓ Managing team members and permissions</li>
              <li>✓ Automating lead assignment</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Step 1: Understanding Your Dashboard</h2>
      <p>
        After logging in, you'll see your main dashboard which provides an overview of:
      </p>
      <ul>
        <li><strong>Total Leads</strong> - Count of all leads in your system</li>
        <li><strong>New Leads</strong> - Leads captured in the last 24 hours</li>
        <li><strong>Conversion Rate</strong> - Percentage of leads moving through your pipeline</li>
        <li><strong>Active Deals</strong> - Leads currently in your sales pipeline</li>
        <li><strong>Lead Sources</strong> - Distribution of leads by source (phone, web, social, etc.)</li>
      </ul>

      <h2>Step 2: Configure Your Pipeline</h2>
      <p>
        Before capturing leads, set up your sales pipeline stages. Navigate to{' '}
        <code>Settings → Pipeline</code>
      </p>

      <h3>Default Pipeline Stages</h3>
      <p>Your CRM comes with these default stages:</p>
      <ol>
        <li><strong>New</strong> - Freshly captured leads</li>
        <li><strong>Contacted</strong> - First contact made</li>
        <li><strong>Qualified</strong> - Lead meets your criteria</li>
        <li><strong>Proposal</strong> - Proposal/quote sent</li>
        <li><strong>Negotiation</strong> - Price/terms discussion</li>
        <li><strong>Won</strong> - Deal closed successfully</li>
        <li><strong>Lost</strong> - Deal didn't close</li>
      </ol>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Customize stages to match your sales process. You can add, edit, or remove stages at any time.
        </p>
      </div>

      <h2>Step 3: Set Up Your First Integration</h2>
      <p>
        Now it's time to start capturing leads! Choose an integration based on your lead source:
      </p>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Phone Integration</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Capture leads from incoming calls via Knowlarity, Mcube, Ozonetel, or TCPN
          </p>
          <a
            href="/docs/integrations/phone"
            className="inline-flex items-center text-sm text-brandColor dark:text-green-400 hover:underline"
          >
            Setup Guide <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">WhatsApp Integration</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Capture leads from WhatsApp Business via WATI or AiSensy
          </p>
          <a
            href="/docs/integrations/whatsapp"
            className="inline-flex items-center text-sm text-brandColor dark:text-green-400 hover:underline"
          >
            Setup Guide <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Facebook Pages</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Capture leads from Facebook Lead Ads automatically
          </p>
          <a
            href="/docs/integrations/facebook"
            className="inline-flex items-center text-sm text-brandColor dark:text-green-400 hover:underline"
          >
            Setup Guide <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Website Forms</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Capture leads from your website contact forms
          </p>
          <a
            href="/docs/integrations/website"
            className="inline-flex items-center text-sm text-brandColor dark:text-green-400 hover:underline"
          >
            Setup Guide <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      </div>

      <h2>Step 4: Configure Round Robin Assignment</h2>
      <p>
        Automatically distribute new leads among your team members. Go to{' '}
        <code>Settings → Round Robin</code>
      </p>

      <h3>How It Works</h3>
      <ol>
        <li>Create a Round Robin Group (e.g., "Sales Team", "Support Team")</li>
        <li>Add team members to the group</li>
        <li>Set distribution rules:
          <ul>
            <li><strong>Sequential</strong> - Leads distributed one by one in order</li>
            <li><strong>Load Balanced</strong> - Based on current workload</li>
          </ul>
        </li>
        <li>Define triggers:
          <ul>
            <li>By lead source (phone, web, Facebook, etc.)</li>
            <li>By time of day</li>
            <li>By custom criteria</li>
          </ul>
        </li>
      </ol>

      <h2>Step 5: Invite Team Members</h2>
      <p>
        Add your team to collaborate on leads. Navigate to <code>Settings → Users</code>
      </p>

      <h3>User Roles</h3>
      <div className="not-prose space-y-3 my-4">
        <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Super Admin</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Full access to all features, settings, and user management
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <CheckCircle className="w-5 h-5 text-brandColor mt-0.5" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Admin</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Can view all leads, manage team, configure settings (cannot delete users)
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Sales Representative</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Can view and manage only assigned leads, add remarks, make calls
            </p>
          </div>
        </div>
      </div>

      <h2>Step 6: Start Capturing Leads!</h2>
      <p>
        Once your integration is active, leads will automatically flow into your CRM. You'll receive:
      </p>
      <ul>
        <li>📧 Email notifications for new leads (if enabled)</li>
        <li>🔔 Real-time dashboard updates</li>
        <li>📱 Lead assignment notifications</li>
        <li>📊 Automatic activity logging</li>
      </ul>

      <h2>Next Steps</h2>
      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/lead-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Learn Lead Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Understand how to manage, track, and convert leads
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/drip-campaigns"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Set Up Drip Campaigns</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Automate email sequences to nurture your leads
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/pipeline"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Master Your Pipeline</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Learn how to move leads through your sales stages
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              You're All Set!
            </h3>
            <p className="text-green-800 dark:text-green-300">
              Your CRM is now configured and ready to capture leads. If you need any help, check out our
              detailed feature documentation or contact support at{' '}
              <a href="mailto:support@yourcrm.com" className="underline">
                support@yourcrm.com
              </a>
            </p>
          </div>
        </div>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStarted;
